import { useState, useEffect } from 'react';
import { getFollowups, createFollowup, updateFollowup, deleteFollowup, getUsers, getInquiries, getOrders, getEditHistory } from '../services/api';
import CustomDropdown from './CustomDropdown';
import CommentSection from './CommentSection';
import { useResponsive } from '../hooks/useResponsive';
import { getModalStyles, getCardGridStyles } from '../utils/responsiveStyles';
import { canDelete } from '../utils/auth';

function Followups() {
  const [followups, setFollowups] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [expandedFollowup, setExpandedFollowup] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    assignedTo: '',
    referenceType: '',
    referenceId: '',
    referenceNumber: ''
  });

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const { isMobile } = useResponsive();
  const modalStyles = getModalStyles(isMobile);
  const cardGridStyles = getCardGridStyles(isMobile);

  useEffect(() => {
    loadFollowups();
    loadUsers();
  }, []);

  const loadFollowups = async () => {
    try {
      const data = await getFollowups();
      setFollowups(data);
    } catch (error) {
      alert('Error loading follow-ups: ' + error.message);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Load edit history when expandedFollowup changes
  useEffect(() => {
    const loadEditHistory = async () => {
      if (expandedFollowup && expandedFollowup.id) {
        try {
          const history = await getEditHistory('followups', expandedFollowup.id);
          setEditHistory(history);
        } catch (error) {
          console.error('Error loading edit history:', error);
          setEditHistory([]);
        }
      } else {
        setEditHistory([]);
      }
    };

    loadEditHistory();
  }, [expandedFollowup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFollowup) {
        await updateFollowup(editingFollowup.id, formData);
      } else {
        await createFollowup(formData);
      }
      setShowForm(false);
      setEditingFollowup(null);
      setFormData({
        title: '',
        description: '',
        dueDate: '',
        assignedTo: '',
        referenceType: '',
        referenceId: '',
        referenceNumber: ''
      });
      loadFollowups();
    } catch (error) {
      alert('Error saving follow-up: ' + error.message);
    }
  };

  const handleEdit = (followup) => {
    setEditingFollowup(followup);
    setFormData({
      title: followup.title,
      description: followup.description || '',
      dueDate: followup.dueDate,
      assignedTo: followup.assignedTo,
      referenceType: followup.referenceType,
      referenceId: followup.referenceId,
      referenceNumber: followup.referenceNumber
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this follow-up?')) return;
    try {
      await deleteFollowup(id);
      loadFollowups();
    } catch (error) {
      alert('Error deleting follow-up: ' + error.message);
    }
  };

  const handleComplete = async (followup) => {
    try {
      await updateFollowup(followup.id, { status: 'completed' });
      loadFollowups();
    } catch (error) {
      alert('Error completing follow-up: ' + error.message);
    }
  };

  const getFilteredFollowups = () => {
    let filtered = followups;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(f => f.status === filterStatus);
    }

    if (filterUser !== 'all') {
      filtered = filtered.filter(f => f.assignedTo === filterUser);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(f => {
        const title = (f.title || '').toLowerCase();
        const description = (f.description || '').toLowerCase();
        const referenceNumber = (f.referenceNumber || '').toLowerCase();
        const userName = getUserName(f.assignedTo).toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        return title.includes(searchLower) ||
               description.includes(searchLower) ||
               referenceNumber.includes(searchLower) ||
               userName.includes(searchLower);
      });
    }

    // Month filter
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(f => {
        const followupDate = new Date(f.createdAt);
        const [year, month] = selectedMonth.split('-');
        return followupDate.getFullYear() === parseInt(year) &&
               followupDate.getMonth() === parseInt(month) - 1;
      });
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#27ae60';
      case 'overdue':
        return '#e74c3c';
      default:
        return '#f39c12';
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  const buttonStyle = {
    padding: '8px 16px',
    background: '#C8342B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  };

  const filteredFollowups = getFilteredFollowups();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#3D1F1F' }}>Follow-ups</h1>
        <button
          onClick={() => {
            setEditingFollowup(null);
            setFormData({
              title: '',
              description: '',
              dueDate: '',
              assignedTo: '',
              referenceType: '',
              referenceId: '',
              referenceNumber: ''
            });
            setShowForm(true);
          }}
          style={buttonStyle}
        >
          Create Follow-up
        </button>
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <div style={{ flex: isMobile ? '1 1 100%' : 2 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by title, description, reference, or user..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 50%' : 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>
            Status
          </label>
          <CustomDropdown
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'completed', label: 'Completed' },
              { value: 'overdue', label: 'Overdue' }
            ]}
            placeholder="All"
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 50%' : 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>
            Assigned To
          </label>
          <CustomDropdown
            value={filterUser}
            onChange={(value) => setFilterUser(value)}
            options={[
              { value: 'all', label: 'All Users' },
              ...users.map(user => ({ value: user.id, label: user.name }))
            ]}
            placeholder="All Users"
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#7f8c8d' }}>
            Month
          </label>
          <CustomDropdown
            value={selectedMonth}
            onChange={(value) => setSelectedMonth(value)}
            options={[
              { value: 'all', label: 'All Months' },
              ...(() => {
                const months = [];
                const now = new Date();
                for (let i = 0; i < 12; i++) {
                  const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  months.push({ value: `${year}-${month}`, label });
                }
                return months;
              })()
            ]}
            placeholder="All Months"
          />
        </div>
      </div>

      {/* Follow-ups List */}
      {filteredFollowups.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ color: '#7f8c8d', fontSize: '16px' }}>
            No follow-ups found. Create your first follow-up!
          </p>
        </div>
      ) : (
        <div style={cardGridStyles}>
          {filteredFollowups.map(followup => {
            const overdue = isOverdue(followup.dueDate, followup.status);
            const actualStatus = overdue && followup.status !== 'completed' ? 'overdue' : followup.status;

            return (
              <div
                key={followup.id}
                onClick={() => setExpandedFollowup(followup)}
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: `2px solid ${getStatusColor(actualStatus)}`,
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, color: '#3D1F1F', fontSize: '16px' }}>{followup.title}</h3>
                    <span style={{
                      padding: '4px 10px',
                      background: getStatusColor(actualStatus),
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase'
                    }}>
                      {overdue && followup.status !== 'completed' ? 'OVERDUE' : actualStatus}
                    </span>
                  </div>
                  {followup.description && (
                    <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '8px 0' }}>
                      {followup.description}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '15px', paddingTop: '15px', borderTop: '1px solid #ecf0f1' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '12px' }}>
                    <div>
                      <div style={{ color: '#7f8c8d', marginBottom: '3px' }}>Due Date</div>
                      <div style={{ fontWeight: '600', color: overdue && followup.status !== 'completed' ? '#e74c3c' : '#3D1F1F' }}>
                        {new Date(followup.dueDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#7f8c8d', marginBottom: '3px' }}>Assigned To</div>
                      <div style={{ fontWeight: '600', color: '#3D1F1F' }}>
                        {getUserName(followup.assignedTo)}
                      </div>
                    </div>
                  </div>
                  {followup.referenceNumber && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ color: '#7f8c8d', marginBottom: '3px', fontSize: '12px' }}>Reference</div>
                      <div style={{ fontWeight: '600', color: '#3D1F1F', fontSize: '12px' }}>
                        {followup.referenceType.toUpperCase()}: {followup.referenceNumber}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {followup.status !== 'completed' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleComplete(followup);
                      }}
                      style={{
                        ...buttonStyle,
                        background: '#27ae60',
                        padding: '6px 12px',
                        fontSize: '12px'
                      }}
                    >
                      Complete
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(followup);
                    }}
                    style={{
                      ...buttonStyle,
                      background: '#3498db',
                      padding: '6px 12px',
                      fontSize: '12px'
                    }}
                  >
                    Edit
                  </button>
                  {canDelete() && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(followup.id);
                      }}
                      style={{
                        ...buttonStyle,
                        background: '#e74c3c',
                        padding: '6px 12px',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setShowForm(false);
            setEditingFollowup(null);
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>{editingFollowup ? 'Edit Follow-up' : 'Create New Follow-up'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Title <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Due Date <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Assigned To <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <CustomDropdown
                  value={formData.assignedTo}
                  onChange={(value) => setFormData({ ...formData, assignedTo: value })}
                  options={users.map(user => ({ value: user.id, label: user.name }))}
                  placeholder="Select User"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingFollowup(null);
                  }}
                  style={{
                    ...buttonStyle,
                    background: '#95a5a6'
                  }}
                >
                  Cancel
                </button>
                <button type="submit" style={buttonStyle}>
                  {editingFollowup ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal with Comments */}
      {expandedFollowup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setExpandedFollowup(null)}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h2 style={{ marginTop: 0, color: '#3D1F1F' }}>{expandedFollowup.title}</h2>
              <button
                onClick={() => setExpandedFollowup(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#7f8c8d',
                  padding: '0 5px'
                }}
              >
                ×
              </button>
            </div>

            {/* Follow-up Details */}
            <div style={{ marginBottom: '25px' }}>
              {expandedFollowup.description && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px' }}>Description</div>
                  <div style={{ color: '#3D1F1F', fontSize: '14px' }}>{expandedFollowup.description}</div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px' }}>Status</div>
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: expandedFollowup.status === 'completed' ? '#27ae60' :
                               isOverdue(expandedFollowup.dueDate, expandedFollowup.status) ? '#e74c3c' : '#f39c12',
                    color: 'white'
                  }}>
                    {expandedFollowup.status === 'completed' ? 'Completed' :
                     isOverdue(expandedFollowup.dueDate, expandedFollowup.status) ? 'Overdue' : 'Pending'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px' }}>Due Date</div>
                  <div style={{ color: '#3D1F1F', fontSize: '14px' }}>
                    {new Date(expandedFollowup.dueDate).toLocaleDateString()}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px' }}>Assigned To</div>
                  <div style={{ color: '#3D1F1F', fontSize: '14px' }}>
                    {users.find(u => u.id === expandedFollowup.assignedTo)?.name || 'N/A'}
                  </div>
                </div>

                {expandedFollowup.referenceType && expandedFollowup.referenceNumber && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px' }}>Reference</div>
                    <div style={{ color: '#3D1F1F', fontSize: '14px' }}>
                      {expandedFollowup.referenceType.toUpperCase()}: {expandedFollowup.referenceNumber}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Edit History Section */}
            {editHistory && editHistory.length > 0 && (
              <div style={{ marginBottom: '25px', borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3D1F1F', fontSize: '16px' }}>Edit History</h3>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {editHistory.map((entry, index) => (
                    <div key={entry.id || index} style={{
                      padding: '10px',
                      background: '#f8f9fa',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      fontSize: '13px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span style={{ fontWeight: '600', color: '#3D1F1F' }}>
                          {entry.fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span style={{ color: '#7f8c8d', fontSize: '12px' }}>
                          {new Date(entry.changedAt).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <span style={{ color: '#e74c3c', textDecoration: 'line-through' }}>
                          {entry.oldValue || '(empty)'}
                        </span>
                        <span>→</span>
                        <span style={{ color: '#27ae60', fontWeight: '500' }}>
                          {entry.newValue || '(empty)'}
                        </span>
                      </div>
                      <div style={{ marginTop: '5px', color: '#7f8c8d', fontSize: '11px' }}>
                        Changed by: {entry.changedByName || 'Unknown'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#3D1F1F', fontSize: '16px' }}>Comments</h3>
              <CommentSection
                referenceType="followup"
                referenceId={expandedFollowup.id}
                currentUserId={currentUser.id}
                currentUserName={currentUser.name}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Followups;
